import clsx from "clsx";
import { ArrowRight, Check, Loader2, Sparkles, X, Zap } from "lucide-react";
import type { PlanScore, StandMove } from "@yb/core";
import type { OptimizeDayPreview, OptimizePreview, RecoveryPreview } from "../lib/api";
import { DateTime } from "luxon";
import { addDays, longDate } from "../lib/format";
import { Kpi, QualityRing } from "./Kpis";

function MoveChips({ moves, max = 60 }: { moves: StandMove[]; max?: number }) {
  return (
    <div className="flex flex-wrap gap-1">
      {moves.slice(0, max).map((m) => (
        <span key={m.turnId} className="tabular rounded-md border border-line bg-panel px-1.5 py-0.5 text-[11px]">
          <b>{m.label}</b> <span className="text-ink-3">{m.from ?? "—"}</span>
          <ArrowRight className="mx-0.5 inline size-3 text-ink-3" />
          <span className="font-semibold">{m.to ?? "unallocated"}</span>
        </span>
      ))}
      {moves.length > max && <span className="text-[11px] text-ink-3">+{moves.length - max} more</span>}
    </div>
  );
}

function Deltas({ before, after }: { before: PlanScore; after: PlanScore }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
      <Kpi
        label="Conflicts"
        value={after.hardConflicts + after.unallocated}
        tone={after.hardConflicts + after.unallocated ? "bad" : "ok"}
        delta={{ value: after.hardConflicts + after.unallocated - (before.hardConflicts + before.unallocated), better: "down" }}
      />
      <Kpi label="Jet bridge" value={`${after.jetbridgePaxPct}%`} delta={{ value: after.jetbridgePaxPct - before.jetbridgePaxPct, better: "up", suffix: "pt" }} />
      <Kpi
        label="Preferences"
        value={`${after.preferenceMet}/${after.preferenceTotal}`}
        delta={{ value: after.preferenceMet - before.preferenceMet, better: "up" }}
      />
      <Kpi label="Tight gaps" value={after.tightTurns} delta={{ value: after.tightTurns - before.tightTurns, better: "down" }} />
    </div>
  );
}

export function OptimizeSheet({
  preview,
  loading,
  applying,
  onApply,
  onDiscard,
}: {
  preview: OptimizePreview | null;
  loading: boolean;
  applying: boolean;
  onApply: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="border-t-4 border-brand bg-panel shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.35)]">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4">
        <div className="flex items-center gap-2">
          <div className="grid size-9 place-items-center rounded-lg bg-brand text-brand-ink">
            {loading ? <Loader2 className="size-5 animate-spin" /> : <Sparkles className="size-5" />}
          </div>
          <div>
            <div className="text-[15px] font-bold">{loading ? "Optimizing the day…" : "Optimized plan ready"}</div>
            {preview && (
              <div className="text-[12px] text-ink-3">
                {preview.turnsConsidered} turns in <b className="text-ink-2">{preview.elapsedMs} ms</b> · locked turns and aircraft already on
                stand were kept
              </div>
            )}
          </div>
        </div>
        {preview && (
          <>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <QualityRing value={preview.before.score.quality} size={48} />
                <div className="mt-0.5 text-[10px] text-ink-3">Now</div>
              </div>
              <ArrowRight className="size-5 text-ink-3" />
              <div className="text-center">
                <QualityRing value={preview.after.score.quality} size={58} />
                <div className="mt-0.5 text-[10px] font-semibold text-ink-2">Optimized</div>
              </div>
            </div>
            <div className="min-w-[320px] flex-1">
              <Deltas before={preview.before.score} after={preview.after.score} />
            </div>
          </>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={onDiscard} className="rounded-lg border border-line px-3 py-2 text-[13px] font-semibold hover:border-ink-3">
            Discard
          </button>
          <button
            onClick={onApply}
            disabled={!preview || applying || preview.diff.moves.length === 0}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-[13px] font-bold text-brand-ink hover:brightness-95 disabled:opacity-50"
          >
            {applying ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            {preview && preview.diff.moves.length === 0 ? "Already optimal" : `Apply ${preview?.diff.moves.length ?? ""} move${preview?.diff.moves.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
      {preview && preview.diff.moves.length > 0 && (
        <div className="scroll-thin max-h-28 overflow-auto border-t border-line px-4 py-2">
          <MoveChips moves={preview.diff.moves} />
        </div>
      )}
    </div>
  );
}

/** How many days a right-click on Optimize offers, counting the day on the board. */
export const OPTIMIZE_SPANS = [1, 2, 3, 7];

/** Right-click on Optimize: the day on the board, or it and the days after. */
export function OptimizeDaysMenu({ date, tz, onPick, onClose }: { date: string; tz: string; onPick: (days: number) => void; onClose: () => void }) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} onContextMenu={(e) => (e.preventDefault(), onClose())} />
      <div className="absolute top-full right-0 z-50 mt-1 w-72 overflow-hidden rounded-xl border border-line bg-panel py-1 shadow-2xl">
        <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Optimize</div>
        {OPTIMIZE_SPANS.map((n) => (
          <button key={n} onClick={() => onPick(n)} className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-panel-2">
            <span className="font-semibold">{n === 1 ? "This day only" : `${n} days`}</span>
            <span className="ml-auto text-[11.5px] text-ink-3">
              {n === 1 ? longDate(date, tz) : `${shortDay(date, tz)} – ${shortDay(addDays(date, n - 1), tz)}`}
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

const shortDay = (date: string, tz: string) => DateTime.fromISO(date, { zone: tz }).toFormat("ccc d LLL");

/** Several days optimized at once: what each day gains, applied together or not at all. */
export function OptimizeDaysSheet({
  days,
  count,
  tz,
  onShow,
  shown,
  applying,
  onApply,
  onDiscard,
}: {
  days: OptimizeDayPreview[] | null;
  count: number;
  tz: string;
  /** Opens a day on the board, where its moves are drawn. */
  onShow: (date: string) => void;
  shown: string;
  applying: boolean;
  onApply: () => void;
  onDiscard: () => void;
}) {
  const moves = days?.reduce((n, d) => n + d.diff.moves.length, 0) ?? 0;
  const changing = days?.filter((d) => d.diff.moves.length).length ?? 0;
  return (
    <div className="border-t-4 border-brand bg-panel shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.35)]">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4">
        <div className="flex items-center gap-2">
          <div className="grid size-9 place-items-center rounded-lg bg-brand text-brand-ink">
            {days ? <Sparkles className="size-5" /> : <Loader2 className="size-5 animate-spin" />}
          </div>
          <div>
            <div className="text-[15px] font-bold">{days ? `${count} days optimized` : `Optimizing ${count} days…`}</div>
            <div className="text-[12px] text-ink-3">
              Each day from its own plan · locked turns and aircraft already on stand were kept · select a day to see its moves on the board
            </div>
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={onDiscard} className="rounded-lg border border-line px-3 py-2 text-[13px] font-semibold hover:border-ink-3">
            Discard
          </button>
          <button
            onClick={onApply}
            disabled={!days || applying || moves === 0}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-[13px] font-bold text-brand-ink hover:brightness-95 disabled:opacity-50"
          >
            {applying ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            {days && moves === 0 ? "Already optimal" : `Apply ${moves || ""} move${moves === 1 ? "" : "s"}${changing > 1 ? ` over ${changing} days` : ""}`}
          </button>
        </div>
      </div>
      {days && (
        <div className="scroll-thin max-h-48 overflow-auto border-t border-line">
          <table className="w-full text-[12.5px]">
            <tbody>
              {days.map((d) => (
                <tr
                  key={d.date}
                  onClick={() => onShow(d.date)}
                  className={clsx("cursor-pointer border-b border-line align-top last:border-0 hover:bg-panel-2", d.date === shown && "bg-panel-2")}
                >
                  <td className="w-40 px-4 py-2 font-semibold whitespace-nowrap">{longDate(d.date, tz)}</td>
                  <td className="tabular w-28 px-2 py-2 whitespace-nowrap">
                    Quality {d.before.score.quality} <ArrowRight className="inline size-3 text-ink-3" /> <b>{d.after.score.quality}</b>
                  </td>
                  <td className="tabular w-24 px-2 py-2 whitespace-nowrap text-ink-2">
                    {d.after.score.hardConflicts + d.after.score.unallocated} conflict{d.after.score.hardConflicts + d.after.score.unallocated === 1 ? "" : "s"}
                  </td>
                  <td className="px-2 py-2">{d.diff.moves.length ? <MoveChips moves={d.diff.moves} max={10} /> : <span className="text-ink-3">No change</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function RecoverySheet({
  preview,
  loading,
  selected,
  onSelect,
  applying,
  onApply,
  onDiscard,
  title,
}: {
  preview: RecoveryPreview | null;
  loading: boolean;
  selected: string | null;
  onSelect: (id: string) => void;
  applying: boolean;
  onApply: () => void;
  onDiscard: () => void;
  title: string;
}) {
  const opt = preview?.options.find((o) => o.id === selected);
  return (
    <div className="border-t-4 border-warn bg-panel shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.35)]">
      <div className="flex items-center gap-3 px-4 pt-3">
        <div className="grid size-9 place-items-center rounded-lg bg-warn text-white">
          {loading ? <Loader2 className="size-5 animate-spin" /> : <Zap className="size-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-bold">{preview?.disruption ?? title}</div>
          <div className="text-[12px] text-ink-3">
            {loading
              ? "Working out recovery options…"
              : preview
                ? `Doing nothing leaves ${preview.doNothing.score.hardConflicts + preview.doNothing.score.unallocated} hard conflicts (quality ${preview.doNothing.score.quality}). ${preview.options.length} option${preview.options.length === 1 ? "" : "s"} found in ${preview.elapsedMs} ms.`
                : ""}
          </div>
        </div>
        <button onClick={onDiscard} className="rounded p-1.5 text-ink-3 hover:bg-panel-2" aria-label="Close">
          <X className="size-4" />
        </button>
      </div>
      {preview && (
        <div className="grid gap-2 p-3 sm:grid-cols-2 xl:grid-cols-3">
          {preview.options.map((o) => (
            <button
              key={o.id}
              onClick={() => onSelect(o.id)}
              className={clsx(
                "rounded-xl border-2 p-3 text-left transition",
                selected === o.id ? "border-ink bg-panel-2" : "border-line hover:border-ink-3",
              )}
            >
              <div className="flex items-center gap-3">
                <QualityRing value={o.score.quality} size={42} />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-bold">{o.title}</div>
                  <div className="text-[11.5px] text-ink-3">{o.description}</div>
                </div>
              </div>
              <div className="tabular mt-2 grid grid-cols-3 gap-2 text-[11px]">
                <div>
                  <div className="text-ink-3">Moves</div>
                  <div className="text-[15px] font-bold">{o.moves.length}</div>
                </div>
                <div>
                  <div className="text-ink-3">Conflicts</div>
                  <div className={clsx("text-[15px] font-bold", o.score.hardConflicts + o.score.unallocated ? "text-bad" : "text-ok")}>
                    {o.score.hardConflicts + o.score.unallocated}
                  </div>
                </div>
                <div>
                  <div className="text-ink-3">Jet bridge</div>
                  <div className="text-[15px] font-bold">{o.score.jetbridgePaxPct}%</div>
                </div>
              </div>
              {o.moves.length > 0 && (
                <div className="mt-2 truncate text-[11px] text-ink-2">
                  {o.moves
                    .slice(0, 4)
                    .map((m) => `${m.label} → ${m.to ?? "—"}`)
                    .join(" · ")}
                  {o.moves.length > 4 && ` · +${o.moves.length - 4}`}
                </div>
              )}
            </button>
          ))}
          {preview.options.length === 0 && <div className="p-3 text-[13px] text-ink-3">No better plan exists with the current rules.</div>}
        </div>
      )}
      {preview && (
        <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-2">
          <span className="mr-auto text-[12px] text-ink-3">The board is showing the selected option. Dashed outlines are where aircraft were.</span>
          <button onClick={onDiscard} className="rounded-lg border border-line px-3 py-2 text-[13px] font-semibold hover:border-ink-3">
            Keep current plan
          </button>
          <button
            onClick={onApply}
            disabled={!opt || applying}
            className="flex items-center gap-1.5 rounded-lg bg-ink px-4 py-2 text-[13px] font-bold text-panel hover:opacity-90 disabled:opacity-50"
          >
            {applying ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            Apply {opt?.title.toLowerCase() ?? "option"}
          </button>
        </div>
      )}
    </div>
  );
}
